import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import { useAuthenticator } from '@aws-amplify/ui-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
    FaTimes, FaExpand, FaCompress, FaTrash, FaChevronDown,
    FaPaperclip, FaArrowUp, FaSpinner, FaFileExcel,
    FaCamera
} from 'react-icons/fa';
import { useTranslation } from '../../i18n';
import useFileReferences from '../../hooks/useFileReferences';
import useActivityTracker from '../../hooks/useActivityTracker';
import FileReferenceDropdown from '../chatbotui/FileReferenceDropdown';
import cliaLogo from '../../assets/img/clia-logo.png';
// Logo por defecto para tenants que no son Cantabria (la marca CLIA es exclusiva).
import defaultLogo from '../../assets/img/logo-data-new.png';
import './FloatingChat.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

/**
 * Limpia la respuesta del agente removiendo marcadores de formato :::TEXTO:::
 * @param {string} text - Texto a limpiar
 * @returns {string} - Texto limpio
 */
const cleanAgentResponse = (text) => {
    if (!text) return text;
    // Remueve cualquier patrón :::ALGO::: (palabra clave del agente)
    return text.replace(/:::[^:]+:::/g, '').trim();
};

// Bot Stars Icon
const BotStarsIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M12 5L14 12L12 19L10 12L12 5Z" />
        <path d="M5 12L12 14L19 12L12 10L5 12Z" />
        <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
        <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
        <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
        <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
    </svg>
);

const FloatingChat = ({ userFeatures = {}, isCantabria = false }) => {
    const { t, language } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuthenticator((context) => [context.user]);
    const username = user?.username;
    const { trackAction } = useActivityTracker(username, 'floating_chat');

    // Chat state
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState('');
    const [thinkingMessage, setThinkingMessage] = useState('');
    const [showThinking, setShowThinking] = useState(false);

    // Conversation state
    const [conversationId, setConversationId] = useState(null);

    // File upload state
    const [uploadedFile, setUploadedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // File references with @ hook
    const {
        availableFiles,
        filteredFiles,
        referencedFiles,
        showFileDropdown,
        fileSearchText,
        selectedFileIndex,
        detectAtReference,
        updateReferencedFiles,
        insertFileReference,
        fetchAllReferencedContent,
        handleKeyNavigation,
        setShowFileDropdown,
        setSelectedFileIndex
    } = useFileReferences(username, { enabled: userFeatures.fileUpload !== false });

    // Screenshot state
    const [isTakingScreenshot, setIsTakingScreenshot] = useState(false);

    // Refs
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Scroll to bottom
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingMessage, thinkingMessage, scrollToBottom]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Initialize with welcome message
    useEffect(() => {
        if (messages.length === 0) {
            setMessages([{
                id: uuidv4(),
                type: 'bot',
                content: t('floatingChat.welcome'),
                timestamp: new Date()
            }]);
        }
    }, [messages.length, t]);

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const clearChat = () => {
        setMessages([{
            id: uuidv4(),
            type: 'bot',
            content: t('floatingChat.cleared'),
            timestamp: new Date()
        }]);
        setStreamingMessage('');
        setThinkingMessage('');
        setShowThinking(false);
        setUploadedFile(null);
        setImagePreview(null);
        setConversationId(null);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const base64 = event.target.result.split(',')[1];
            const fileType = file.type.startsWith('image/') ? 'image' :
                           file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? 'excel' :
                           file.name.endsWith('.csv') ? 'csv' :
                           file.name.endsWith('.pdf') ? 'pdf' : 'unknown';

            setUploadedFile({
                filename: file.name,
                type: fileType,
                base64: base64,
                mimeType: file.type
            });

            if (fileType === 'image') {
                setImagePreview(event.target.result);
            }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const removeFile = () => {
        setUploadedFile(null);
        setImagePreview(null);
    };

    // Ensure a conversation exists (create one if needed)
    const ensureConversation = async () => {
        if (conversationId) return conversationId;

        try {
            const res = await fetch(`${STRANDS_API_URL}/add_conversation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_user: username, type_name: 'text' })
            });
            const data = await res.json();
            if (data?.id_conversation) {
                setConversationId(data.id_conversation);
                console.log('[FloatingChat] Conversación creada:', data.id_conversation);
                return data.id_conversation;
            }
        } catch (error) {
            console.error('[FloatingChat] Error creando conversación:', error);
        }
        return null;
    };

    // Add a message to the database
    const addMessageBD = async (convId, content, author, messageId) => {
        try {
            await fetch(`${STRANDS_API_URL}/add_message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_user: username,
                    id_conversation: convId,
                    content: typeof content === 'string' ? content : 'Message',
                    author: author,
                    id_message: messageId
                })
            });
        } catch (error) {
            console.error('[FloatingChat] Error guardando mensaje:', error);
        }
    };

    // Generate conversation title
    const generateTitle = async (convId, firstMessage) => {
        try {
            await fetch(`${STRANDS_API_URL}/conversations/${convId}/generate-title`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    first_message: firstMessage,
                    username: username
                })
            });
            // Notify sidebar to refresh
            document.dispatchEvent(new CustomEvent('conversationCreated'));
        } catch (error) {
            console.error('[FloatingChat] Error generando título:', error);
        }
    };

    // Open full chat page with current conversation
    const openFullChat = () => {
        if (conversationId) {
            navigate(`/chat/${conversationId}`);
        }
        setIsOpen(false);
    };

    // Take screenshot using Screen Capture API (captures real pixels including PowerBI/iframes)
    const takeScreenshot = useCallback(async () => {
        if (isTakingScreenshot) return;
        setIsTakingScreenshot(true);

        try {
            // Close the chat temporarily to capture the page without it
            setIsOpen(false);

            // Wait for the chat to close
            await new Promise(resolve => setTimeout(resolve, 300));

            // Request screen capture permission
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: 'browser', // Prefer browser tab
                },
                preferCurrentTab: true // Hint to select current tab
            });

            // Get the video track
            const videoTrack = stream.getVideoTracks()[0];
            const imageCapture = new ImageCapture(videoTrack);

            // Capture a frame
            const bitmap = await imageCapture.grabFrame();

            // Draw to canvas
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(bitmap, 0, 0);

            // Stop the stream immediately after capture
            stream.getTracks().forEach(track => track.stop());

            // Convert canvas to base64
            const base64Image = canvas.toDataURL('image/png').split(',')[1];
            const timestamp = new Date().toLocaleTimeString('es-MX', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });

            // Set the screenshot as the uploaded file
            setUploadedFile({
                filename: `screenshot_${timestamp.replace(/:/g, '-')}.png`,
                type: 'image',
                base64: base64Image,
                mimeType: 'image/png'
            });

            // Set preview
            setImagePreview(`data:image/png;base64,${base64Image}`);

            // Reopen the chat with the screenshot ready
            setIsOpen(true);

            // Focus the input for the user to type their question
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);

            console.log('[FloatingChat] Screenshot captured successfully');

        } catch (error) {
            console.error('[FloatingChat] Error taking screenshot:', error);
            // User cancelled or error occurred - reopen chat
            setIsOpen(true);

            // If user just cancelled, don't show as error
            if (error.name !== 'NotAllowedError') {
                console.error('[FloatingChat] Screenshot error details:', error.message);
            }
        } finally {
            setIsTakingScreenshot(false);
        }
    }, []);

    // Send message
    const sendMessage = async () => {
        if (!username) {
            console.error('[FloatingChat] No username');
            return;
        }

        if ((!inputValue.trim() && !uploadedFile) || isLoading) return;

        trackAction('send_query');

        const currentFile = uploadedFile;
        const currentReferencedFiles = [...referencedFiles];
        const userMessage = {
            id: uuidv4(),
            type: 'user',
            content: inputValue || 'Attached file',
            timestamp: new Date(),
            hasFile: !!currentFile,
            fileName: currentFile?.filename,
            fileType: currentFile?.type,
            referencedFiles: currentReferencedFiles
        };

        setMessages(prev => [...prev, userMessage]);
        const queryText = inputValue;
        setInputValue('');
        setIsLoading(true);
        setStreamingMessage('');
        setThinkingMessage('');
        setShowThinking(false);
        setUploadedFile(null);
        setImagePreview(null);

        abortControllerRef.current = new AbortController();

        try {
            // Auto-crear conversación si no existe
            const convId = await ensureConversation();

            // Guardar mensaje del usuario en BD
            if (convId) {
                await addMessageBD(convId, queryText || 'Attached file', 'Client', userMessage.id);
                // Generar título con el primer mensaje
                if (messages.filter(m => m.type === 'user').length === 0) {
                    generateTitle(convId, queryText);
                }
            }

            const payload = {
                username: username,
                query: queryText || 'Analyze the attached file',
                language: language,
                conversation_history: messages.slice(-20).map(m => ({
                    role: m.type === 'user' ? 'user' : 'assistant',
                    content: typeof m.content === 'string' ? m.content : ''
                }))
            };

            // Add file data to payload
            if (currentFile) {
                switch (currentFile.type) {
                    case 'image':
                        payload.image_data = currentFile.base64;
                        payload.image_mime_type = currentFile.mimeType;
                        payload.image_filename = currentFile.filename;
                        break;
                    case 'excel':
                    case 'csv':
                        payload.excel_data = currentFile.base64;
                        payload.excel_filename = currentFile.filename;
                        break;
                    case 'pdf':
                        payload.pdf_data = currentFile.base64;
                        payload.pdf_filename = currentFile.filename;
                        break;
                    default:
                        break;
                }
            }

            // Add referenced files content from @ mentions
            // Convertir a los campos que el backend entiende (excel_data, pdf_data, image_data)
            if (referencedFiles.length > 0) {
                try {
                    const refContents = await fetchAllReferencedContent();
                    if (refContents.length > 0) {
                        for (const refFile of refContents) {
                            if (refFile.content_base64) {
                                if (refFile.file_type === 'csv' || refFile.file_type === 'excel') {
                                    payload.excel_data = refFile.content_base64;
                                    payload.excel_filename = refFile.original_name;
                                } else if (refFile.file_type === 'pdf') {
                                    payload.pdf_data = refFile.content_base64;
                                    payload.pdf_filename = refFile.original_name;
                                } else if (refFile.file_type === 'image') {
                                    payload.image_data = refFile.content_base64;
                                    payload.image_mime_type = refFile.mime_type || 'image/png';
                                    payload.image_filename = refFile.original_name;
                                }
                                console.log(`[FloatingChat] Archivo @ agregado: ${refFile.original_name} (${refFile.file_type})`);
                            }
                        }
                    }
                } catch (err) {
                    console.error('[FloatingChat] Error fetching referenced files:', err);
                }
            }

            console.log('[FloatingChat] Sending to Strands API...');
            const response = await fetch(`${STRANDS_API_URL}/query-stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = '';
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.type === 'start') {
                                setShowThinking(true);
                            } else if (data.type === 'thinking') {
                                setThinkingMessage(data.accumulated || data.content || '');
                            } else if (data.type === 'final_response_start') {
                                setShowThinking(false);
                                setThinkingMessage('');
                                accumulatedText = '';
                            } else if (data.type === 'chunk') {
                                accumulatedText = data.accumulated || accumulatedText + (data.content || '');
                                setStreamingMessage(cleanAgentResponse(accumulatedText));
                            } else if (data.type === 'done') {
                                const rawText = data.content || accumulatedText;
                                const finalText = cleanAgentResponse(rawText);
                                const botMessageId = uuidv4();
                                setMessages(prev => [...prev, {
                                    id: botMessageId,
                                    type: 'bot',
                                    content: finalText,
                                    timestamp: new Date()
                                }]);
                                // Guardar respuesta del bot en BD
                                if (convId) {
                                    addMessageBD(convId, finalText, 'Bot', botMessageId);
                                }
                                setStreamingMessage('');
                                setThinkingMessage('');
                                setShowThinking(false);
                            } else if (data.type === 'error') {
                                throw new Error(data.error);
                            }
                        } catch (parseError) {
                            // Ignore parse errors for incomplete JSON
                        }
                    }
                }
            }

            setIsLoading(false);

        } catch (error) {
            console.error('[FloatingChat] Error:', error);
            setIsLoading(false);
            setStreamingMessage('');
            setThinkingMessage('');
            setShowThinking(false);

            if (error.name !== 'AbortError') {
                setMessages(prev => [...prev, {
                    id: uuidv4(),
                    type: 'bot',
                    content: `Error: ${error.message}`,
                    timestamp: new Date(),
                    isError: true
                }]);
            }
        }
    };

    // Handle input change with @ detection
    const handleInputChange = (e) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart;

        setInputValue(value);

        // Detect @ references for files
        detectAtReference(value, cursorPos);

        // Update referenced files in text
        updateReferencedFiles(value);
    };

    // Handle file reference selection from @ dropdown
    const handleFileReferenceSelect = (file) => {
        const cursorPos = inputRef.current?.selectionStart || inputValue.length;
        const { newText, newCursorPosition } = insertFileReference(file, inputValue, cursorPos);
        setInputValue(newText);

        // Update referenced files
        setTimeout(() => {
            updateReferencedFiles(newText);
        }, 0);

        // Focus back on input
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
            }
        }, 10);
    };

    const handleKeyPress = (e) => {
        // Handle file dropdown navigation
        if (showFileDropdown) {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                handleKeyNavigation(e.key);
                return;
            }
            if (e.key === 'Enter') {
                const selectedFile = handleKeyNavigation('Enter');
                if (selectedFile && selectedFile !== 'handled') {
                    e.preventDefault();
                    handleFileReferenceSelect(selectedFile);
                    return;
                }
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowFileDropdown(false);
                return;
            }
        }

        // Normal enter to send
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const formatTimestamp = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Table component with download
    const MarkdownTableWithDownload = ({ children }) => {
        const tableRef = useRef(null);

        const handleDownloadTable = () => {
            try {
                const table = tableRef.current;
                const rows = table.querySelectorAll('tr');
                const data = [];

                rows.forEach((row, index) => {
                    const cells = row.querySelectorAll('th, td');
                    const rowData = {};

                    cells.forEach((cell, cellIndex) => {
                        if (index === 0) {
                            rowData[cell.textContent.trim()] = cell.textContent.trim();
                        } else {
                            const headers = Array.from(table.querySelectorAll('tr:first-child th, tr:first-child td'));
                            const headerText = headers[cellIndex]?.textContent.trim() || `Column_${cellIndex + 1}`;
                            rowData[headerText] = cell.textContent.trim();
                        }
                    });

                    if (index > 0) {
                        data.push(rowData);
                    }
                });

                const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
                const worksheet = XLSX.utils.json_to_sheet(data);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
                XLSX.writeFile(workbook, `table_${timestamp}.xlsx`);
            } catch (error) {
                console.error('Error downloading table:', error);
            }
        };

        return (
            <div className="fc-markdown-table-wrapper">
                {userFeatures.dataExport !== false && (
                <div className="fc-table-header">
                    <button
                        className="fc-download-table-btn"
                        onClick={handleDownloadTable}
                        title={t('chat.downloadTableAsExcel')}
                    >
                        <FaFileExcel size={12} />
                        {t('chat.download')}
                    </button>
                </div>
                )}
                <div className="fc-markdown-table-container">
                    <table className="fc-markdown-table" ref={tableRef}>
                        {children}
                    </table>
                </div>
            </div>
        );
    };

    if (!username) {
        return null;
    }

    return (
        <>
            {/* Floating Action Button */}
            <button
                className={`fc-fab ${isOpen ? 'fc-fab-open' : 'fc-fab-logo'}`}
                onClick={toggleChat}
                aria-label={t('floatingChat.title')}
                title={t('floatingChat.title')}
            >
                {isOpen ? (
                    <FaTimes />
                ) : (
                    // La marca CLIA es exclusiva de Cantabria; el resto usa el logo por defecto.
                    <img src={isCantabria ? cliaLogo : defaultLogo} alt={t('floatingChat.title')} className="fc-fab-img" />
                )}
            </button>

            {/* Chat Panel */}
            <div className={`fc-panel ${isOpen ? 'fc-panel-open' : ''} ${isExpanded ? 'fc-panel-expanded' : ''}`}>
                {/* Header */}
                <div className="fc-header">
                    <div className="fc-header-content">
                        <div className="fc-header-avatar">
                            <BotStarsIcon />
                        </div>
                        <div className="fc-header-info">
                            <h3>{t('floatingChat.title')}</h3>
                            <span className="fc-status">
                                <span className="fc-status-dot"></span>
                                Online
                            </span>
                        </div>
                    </div>
                    <div className="fc-header-actions">
                        <button
                            className="fc-header-btn"
                            onClick={toggleExpand}
                            title={isExpanded ? t('floatingChat.restore') : t('floatingChat.expand')}
                        >
                            {isExpanded ? <FaCompress /> : <FaExpand />}
                        </button>
                        <button
                            className="fc-header-btn"
                            onClick={clearChat}
                            title={t('floatingChat.clear')}
                        >
                            <FaTrash />
                        </button>
                        <button
                            className="fc-header-btn"
                            onClick={toggleChat}
                            title={t('floatingChat.minimize')}
                        >
                            <FaChevronDown />
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div className="fc-messages">
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`fc-message ${message.type === 'user' ? 'fc-message-user' : 'fc-message-bot'} ${message.isError ? 'fc-message-error' : ''}`}
                        >
                            {message.type === 'bot' && (
                                <div className="fc-message-avatar">
                                    <BotStarsIcon />
                                </div>
                            )}
                            <div className="fc-message-content">
                                {message.hasFile && message.fileName && (
                                    <div className="fc-message-file-badge">
                                        <span className="fc-file-icon">
                                            {message.fileType === 'image' ? '🖼️' :
                                             message.fileType === 'excel' || message.fileType === 'csv' ? '📊' :
                                             message.fileType === 'pdf' ? '📄' : '📁'}
                                        </span>
                                        <span className="fc-file-name">{message.fileName}</span>
                                    </div>
                                )}
                                {message.referencedFiles && message.referencedFiles.length > 0 && (
                                    <div className="fc-message-refs">
                                        {message.referencedFiles.map((file, idx) => (
                                            <span key={idx} className="fc-ref-badge">
                                                <span className="fc-ref-icon">@</span>
                                                {file.original_name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="fc-message-text">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            code({ node, inline, className, children, ...props }) {
                                                const match = /language-(\w+)/.exec(className || '');
                                                return !inline && match ? (
                                                    <SyntaxHighlighter
                                                        style={tomorrow}
                                                        language={match[1]}
                                                        PreTag="div"
                                                        {...props}
                                                    >
                                                        {String(children).replace(/\n$/, '')}
                                                    </SyntaxHighlighter>
                                                ) : (
                                                    <code className={className} {...props}>
                                                        {children}
                                                    </code>
                                                );
                                            },
                                            table({ children }) {
                                                return <MarkdownTableWithDownload>{children}</MarkdownTableWithDownload>;
                                            }
                                        }}
                                    >
                                        {message.content}
                                    </ReactMarkdown>
                                </div>
                                <div className="fc-message-footer">
                                    <span className="fc-message-time">
                                        {formatTimestamp(message.timestamp)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Thinking indicator */}
                    {showThinking && thinkingMessage && (
                        <div className="fc-message fc-message-bot fc-message-thinking">
                            <div className="fc-message-avatar">
                                <BotStarsIcon />
                            </div>
                            <div className="fc-message-content">
                                <div className="fc-thinking-label">
                                    <FaSpinner className="fc-spin" />
                                    {t('floatingChat.analyzing')}
                                </div>
                                <div className="fc-message-text fc-thinking-text">
                                    {thinkingMessage}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Streaming message */}
                    {streamingMessage && (
                        <div className="fc-message fc-message-bot fc-message-streaming">
                            <div className="fc-message-avatar">
                                <BotStarsIcon />
                            </div>
                            <div className="fc-message-content">
                                <div className="fc-message-text">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {streamingMessage}
                                    </ReactMarkdown>
                                </div>
                                <span className="fc-typing-cursor">▊</span>
                            </div>
                        </div>
                    )}

                    {/* Loading indicator */}
                    {isLoading && !streamingMessage && !thinkingMessage && (
                        <div className="fc-message fc-message-bot">
                            <div className="fc-message-avatar">
                                <BotStarsIcon />
                            </div>
                            <div className="fc-message-content">
                                <div className="fc-typing">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Container */}
                <div className="fc-input-container">
                    {/* Referenced files from @ */}
                    {referencedFiles.length > 0 && (
                        <div className="fc-referenced-files">
                            {referencedFiles.map((file, idx) => (
                                <span key={idx} className="fc-ref-tag">
                                    <span className="fc-ref-tag-icon">@</span>
                                    {file.original_name}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* File preview */}
                    {uploadedFile && (
                        <div className="fc-file-preview">
                            {imagePreview ? (
                                <img src={imagePreview} alt="Preview" className="fc-image-preview" />
                            ) : (
                                <div className="fc-file-info">
                                    <span className="fc-file-icon-large">
                                        {uploadedFile.type === 'excel' || uploadedFile.type === 'csv' ? '📊' :
                                         uploadedFile.type === 'pdf' ? '📄' : '📁'}
                                    </span>
                                    <span className="fc-file-name-large">{uploadedFile.filename}</span>
                                </div>
                            )}
                            <button className="fc-file-remove" onClick={removeFile}>
                                <FaTimes />
                            </button>
                        </div>
                    )}

                    <div className="fc-input-wrapper">
                        {userFeatures.fileUpload !== false && (
                        <>
                        <button
                            className="fc-attach-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isLoading}
                            title={t('floatingChat.attach')}
                        >
                            <FaPaperclip />
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.xlsx,.xls,.csv,.pdf"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        </>
                        )}
                        <button
                            className="fc-screenshot-btn"
                            onClick={takeScreenshot}
                            disabled={isLoading || isTakingScreenshot}
                            title={t('floatingChat.screenshot')}
                        >
                            {isTakingScreenshot ? <FaSpinner className="fc-spin" /> : <FaCamera />}
                        </button>

                        <textarea
                            ref={inputRef}
                            className="fc-input"
                            placeholder={t('chat.placeholder')}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyPress}
                            rows={1}
                            disabled={isLoading}
                        />

                        {/* File Reference Dropdown */}
                        {userFeatures.fileUpload !== false && (
                        <FileReferenceDropdown
                            isOpen={showFileDropdown}
                            files={filteredFiles}
                            searchText={fileSearchText}
                            onSelect={handleFileReferenceSelect}
                            onClose={() => setShowFileDropdown(false)}
                            selectedIndex={selectedFileIndex}
                            position={{ bottom: '100%', left: '40px' }}
                        />
                        )}

                        <button
                            className="fc-send-btn"
                            onClick={sendMessage}
                            disabled={(!inputValue.trim() && !uploadedFile) || isLoading}
                            title={t('chat.send')}
                        >
                            <FaArrowUp />
                        </button>
                    </div>

                    <p className="fc-input-hint">
                        {t('chat.disclaimer')}
                    </p>
                </div>
            </div>

            {/* Overlay when chat is open */}
            {isOpen && <div className="fc-overlay" onClick={toggleChat} />}
        </>
    );
};

export default FloatingChat;
